import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Check, X, Image as ImageIcon, Loader2 } from "lucide-react";

const UploadInvite = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [name, setName] = useState("");
  const [queue, setQueue] = useState([]); // [{file, title, status: 'pending'|'uploading'|'done'|'error', message?}]
  const inputRef = useRef(null);

  useEffect(() => {
    api.get(`/invites/${token}/verify`)
      .then((r) => setState({ loading: false, invite: r.data, error: null }))
      .catch((e) => {
        const msg = e.response?.data?.detail || "This upload link is invalid.";
        setState({ loading: false, invite: null, error: msg });
      });
  }, [token]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = files.map((f) => ({
      id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      title: f.name.replace(/\.[^.]+$/, ""),
      status: "pending",
      previewUrl: URL.createObjectURL(f),
    }));
    setQueue((q) => [...q, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => {
    setQueue((q) => q.filter((_, i) => i !== idx));
  };

  const updateTitle = (idx, value) => {
    setQueue((q) => q.map((item, i) => (i === idx ? { ...item, title: value } : item)));
  };

  const uploadOne = async (idx) => {
    const item = queue[idx];
    if (!item || item.status === "uploading" || item.status === "done") return;
    setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: "uploading" } : it)));
    try {
      const fd = new FormData();
      fd.append("file", item.file);
      await api.post(`/invites/${token}/upload`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Uploader-Name": encodeURIComponent(name || ""),
          "X-Title": encodeURIComponent(item.title || item.file.name),
        },
      });
      setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: "done" } : it)));
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: "error", message: msg } : it)));
      toast.error(`${item.file.name}: ${msg}`);
    }
  };

  const uploadAll = async () => {
    if (!queue.length) {
      toast.error("Add at least one photo first");
      return;
    }
    let success = 0;
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].status === "done") continue;
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(i);
      success++;
    }
    if (success > 0) toast.success("Photos uploaded. The band will see them in the gallery.");
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white" data-testid="upload-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white px-5" data-testid="upload-invalid">
        <div className="border border-white/15 p-10 max-w-lg w-full text-center">
          <p className="eyebrow text-red-300">Sorry</p>
          <h1 className="font-heading text-4xl uppercase mt-3">Link not active</h1>
          <p className="text-neutral-400 mt-4">{state.error}</p>
          <p className="font-mono text-xs text-neutral-500 mt-8">Ping Seth for a new link.</p>
        </div>
      </div>
    );
  }

  const inv = state.invite;
  const remaining = inv.remaining;

  return (
    <div className="grain min-h-screen bg-neutral-950 text-white" data-testid="upload-page">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-8">
          <p className="eyebrow">Band Photo Drop · Retrofit</p>
          <h1 className="font-heading text-4xl md:text-5xl uppercase mt-3 leading-[0.95]">
            Add to the gallery
          </h1>
          <p className="text-neutral-400 mt-4 max-w-xl">
            {inv.label ? <span className="font-mono text-xs tracking-[0.2em] uppercase text-white">{inv.label} · </span> : null}
            Drop in photos from a show. They land in the public Media page once you hit upload. JPEG · PNG · HEIC · up to 12 MB each. <span className="text-white">{remaining}</span> slots remaining on this link.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-10 py-10">
        <div className="mb-8">
          <label className="label-retro">Your name (optional)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="field"
            placeholder="So Seth knows who to thank"
            data-testid="upload-name"
          />
        </div>

        <div
          className="border-2 border-dashed border-white/30 hover:border-white transition-colors p-10 text-center cursor-pointer"
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
        >
          <Upload className="mx-auto mb-4" size={32} />
          <p className="font-heading text-2xl uppercase">Tap to pick photos</p>
          <p className="text-xs text-neutral-500 mt-2 font-mono tracking-[0.2em] uppercase">JPEG · PNG · HEIC · WEBP</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
            multiple
            className="hidden"
            onChange={onPick}
            data-testid="upload-input"
          />
        </div>

        {queue.length > 0 && (
          <ul className="mt-8 space-y-4" data-testid="upload-queue">
            {queue.map((item, idx) => (
              <li key={item.id} className="border border-white/15 p-4 flex gap-4 items-start" data-testid={`upload-item-${idx}`}>
                <img src={item.previewUrl} alt="" className="w-24 h-24 object-cover" />
                <div className="flex-1 min-w-0">
                  <input
                    value={item.title}
                    onChange={(e) => updateTitle(idx, e.target.value)}
                    className="field !py-1 text-sm"
                    placeholder="Photo title"
                    data-testid={`upload-title-${idx}`}
                  />
                  <p className="text-xs text-neutral-500 mt-2 font-mono truncate">
                    {item.file.name} · {(item.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {item.status === "error" && <p className="text-xs text-red-300 mt-1">{item.message}</p>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {item.status === "pending" && (
                    <button onClick={() => removeAt(idx)} className="text-neutral-400 hover:text-white" data-testid={`upload-remove-${idx}`}><X size={18}/></button>
                  )}
                  {item.status === "uploading" && <Loader2 className="animate-spin" size={18}/>}
                  {item.status === "done" && <Check className="text-white" size={18}/>}
                  {item.status === "error" && <span className="text-xs text-red-300 font-mono">FAIL</span>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
          <p className="font-mono text-xs text-neutral-500">
            {queue.filter((q) => q.status === "done").length} / {queue.length} uploaded
          </p>
          <button
            onClick={uploadAll}
            disabled={queue.length === 0}
            className="btn-retro-solid disabled:opacity-40"
            data-testid="upload-submit"
          >
            <ImageIcon size={14} className="mr-3" /> Upload {queue.length || ""}
          </button>
        </div>

        <p className="mt-10 font-mono text-[10px] text-neutral-600 tracking-[0.2em] uppercase">
          Anything you upload goes to the public Media page. Seth can remove anything later.
        </p>
      </main>
    </div>
  );
};

export default UploadInvite;
